import { 
  collection, 
  query, 
  where, 
  onSnapshot, 
  getDocs,
  doc,
  getDoc,
  Timestamp,
  Unsubscribe 
} from 'firebase/firestore';
import { db } from '@/lib/firebase/config';
import { updateInventoryDatabaseItem, getAllInventory } from './inventoryService';
import { photoLogService } from './photoLogService';
import { getShopifyService } from './shopifyService';
import { WorkflowStatus, InventoryStatus, InventoryDatabaseItem } from '@/types/inventory';
import toast from 'react-hot-toast';

/**
 * InventoryStatusSyncService
 * 
 * Automatically syncs inventory workflow statuses from:
 * 1. Photo Log (when items are photographed/edited)
 * 2. Product Entry (when template status changes)
 * 3. Shopify (when items are listed/sold)
 * 
 * Also provides:
 * - Bulk "Sync All" functionality
 * - Periodic background sync
 */
class InventoryStatusSyncService {
  private static instance: InventoryStatusSyncService;
  private unsubscribers: Unsubscribe[] = [];
  private periodicSyncInterval: NodeJS.Timeout | null = null;
  private isInitialized = false;
  
  // Throttle map to prevent rapid duplicate syncs
  private syncThrottle: Map<string, number> = new Map();
  private readonly THROTTLE_MS = 2000; // 2 seconds between syncs for same SKU

  private constructor() {}

  static getInstance(): InventoryStatusSyncService {
    if (!InventoryStatusSyncService.instance) {
      InventoryStatusSyncService.instance = new InventoryStatusSyncService();
    }
    return InventoryStatusSyncService.instance;
  }

  /**
   * Initialize all real-time listeners and periodic sync
   */
  async initialize(): Promise<void> {
    if (this.isInitialized) {
      console.log('[InventoryStatusSync] Already initialized');
      return;
    }

    console.log('[InventoryStatusSync] Initializing automatic status sync...');
    
    // Set up real-time listeners
    this.setupPhotoLogListener();
    this.setupProductEntryListener();
    
    // DISABLED: Periodic sync was causing counts to change unexpectedly
    // Use manual "Sync All Statuses" button instead
    // this.startPeriodicSync(5 * 60 * 1000);
    
    this.isInitialized = true;
    console.log('[InventoryStatusSync] Initialization complete');
  }

  /**
   * Clean up all listeners and intervals
   */
  cleanup(): void {
    console.log('[InventoryStatusSync] Cleaning up listeners...');
    
    // Unsubscribe from all Firestore listeners
    this.unsubscribers.forEach(unsub => unsub());
    this.unsubscribers = [];
    
    // Clear periodic sync
    if (this.periodicSyncInterval) {
      clearInterval(this.periodicSyncInterval);
      this.periodicSyncInterval = null;
    }
    
    this.isInitialized = false;
    console.log('[InventoryStatusSync] Cleanup complete');
  }

  /**
   * Set up real-time listener for Photo Log changes
   */
  private setupPhotoLogListener(): void {
    try {
      // Listen to photo log changes across all months
      // We'll listen to the last 3 months to catch recent changes
      const now = new Date();
      const months = [
        `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`,
        `${now.getFullYear()}-${String(now.getMonth()).padStart(2, '0') || '12'}`,
        `${now.getFullYear()}-${String(now.getMonth() - 1).padStart(2, '0') || '11'}`
      ];

      months.forEach(month => {
        const monthEntriesRef = collection(db, 'photo_log', month, 'entries');
        
        const unsubscribe = onSnapshot(monthEntriesRef, (snapshot) => {
          snapshot.docChanges().forEach(async (change) => {
            if (change.type === 'added' || change.type === 'modified') {
              const data = change.doc.data();
              const sku = data.sku;
              const status = data.status;
              
              console.log(`[PhotoLog] Status change detected: ${sku} → ${status}`);
              
              // Map photo log status to workflow status
              let workflowStatus: WorkflowStatus | null = null;
              if (status === 'queue') workflowStatus = 'photo_queue';
              else if (status === 'photod') workflowStatus = 'photod';
              else if (status === 'edited') workflowStatus = 'edited';
              
              if (workflowStatus) {
                await this.syncItemBySku(sku, workflowStatus, 'photo_log');
              }
            }
          });
        }, (error) => {
          console.error(`[PhotoLog] Error listening to month ${month}:`, error);
        });

        this.unsubscribers.push(unsubscribe);
      });

      console.log('[InventoryStatusSync] Photo Log listener set up');
    } catch (error) {
      console.error('[InventoryStatusSync] Error setting up Photo Log listener:', error);
    }
  }

  /**
   * Set up real-time listener for Product Entry changes
   */
  private setupProductEntryListener(): void {
    try {
      // Listen to jewelry product entries
      const jewelryRef = collection(db, 'jewelry_products');
      const jewelryEntriesRef = collection(db, 'jewelryProductEntries');
      
      // Listen to both collections (in case both are used)
      const collections = [jewelryRef, jewelryEntriesRef];
      // Only entries changed after the app opened: the first snapshot is ignored anyway, and
      // listening to the whole collection downloaded every entry (~46 MB) on each load.
      // Every status/push write sets updatedAt; the 5-minute cushion covers client clock skew.
      const since = Timestamp.fromMillis(Date.now() - 5 * 60 * 1000);
      
      collections.forEach(collectionRef => {
        let isInitialSnapshot = true;
        const unsubscribe = onSnapshot(query(collectionRef, where('updatedAt', '>', since)), (snapshot) => {
          if (isInitialSnapshot) {
            isInitialSnapshot = false;
            return;
          }
          snapshot.docChanges().forEach(async (change) => {
            if (change.type === 'added' || change.type === 'modified') {
              const data = change.doc.data();
              const sku = data.sku;
              const status = data.status;
              const shopifyPushStatus = data.shopifyPushStatus;
              const shopifyProductId = data.shopifyProductId;
              
              console.log(`[ProductEntry] Status change detected: ${sku} → ${status} (shopifyPush: ${shopifyPushStatus}, shopifyId: ${shopifyProductId || 'none'})`);
              
              // If item has been published to Shopify, mark as listed immediately
              if (shopifyProductId || shopifyPushStatus === 'success' || shopifyPushStatus === 'partial_success' || shopifyPushStatus === 'completed') {
                console.log(`[ProductEntry] Item ${sku} published to Shopify — moving to listed`);
                await this.syncItemBySku(sku, 'listed', 'shopify');
                return;
              }

              // Map product entry status to workflow status
              let workflowStatus: WorkflowStatus | null = null;
              if (status === 'draft') workflowStatus = 'draft';
              else if (status === 'pending' || status === 'pending_review' || status === 'in_review') workflowStatus = 'sent_for_review';
              else if (status === 'needs_revision' || status === 'revision_requested' || status === 'corrections_needed') workflowStatus = 'waiting_corrections';
              else if (status === 'final_review') workflowStatus = 'final_review';
              else if (status === 'partially_approved') workflowStatus = 'partially_approved';
              else if (status === 'approved' || status === 'ready_to_list') workflowStatus = 'approved';
              
              if (workflowStatus) {
                await this.syncItemBySku(sku, workflowStatus, 'product_entry');
              }
            }
          });
        }, (error) => {
          console.error('[ProductEntry] Error listening to product entries:', error);
        });

        this.unsubscribers.push(unsubscribe);
      });

      console.log('[InventoryStatusSync] Product Entry listener set up');
    } catch (error) {
      console.error('[InventoryStatusSync] Error setting up Product Entry listener:', error);
    }
  }

  /**
   * Check Shopify for item status (listed/sold)
   */
  private async checkShopifyStatus(sku: string): Promise<WorkflowStatus | null> {
    try {
      const shopifyService = getShopifyService();
      
      // Query Shopify GraphQL for this SKU
      const response = await (shopifyService as any).request('/graphql.json', {
        method: 'POST',
        body: JSON.stringify({
          query: `query { 
            productVariants(first: 1, query: "sku:${sku}") { 
              edges { 
                node { 
                  inventoryQuantity 
                  product { 
                    status 
                  } 
                } 
              } 
            } 
          }`
        })
      });

      const variant = response?.data?.productVariants?.edges?.[0]?.node;
      if (variant) {
        // If inventory is 0, likely sold
        if (variant.inventoryQuantity === 0) {
          return 'sold';
        }
        // If product exists and is active, it's listed
        if (variant.product?.status === 'ACTIVE' && variant.inventoryQuantity > 0) {
          return 'listed';
        }
      }
      
      return null;
    } catch (error) {
      console.error(`[Shopify] Error checking status for ${sku}:`, error);
      return null;
    }
  }

  /**
   * Sync a single item by SKU with throttling
   */
  private async syncItemBySku(
    sku: string, 
    workflowStatus: WorkflowStatus,
    source: 'photo_log' | 'product_entry' | 'shopify' | 'manual'
  ): Promise<void> {
    // Check throttle
    const lastSync = this.syncThrottle.get(sku);
    if (lastSync && Date.now() - lastSync < this.THROTTLE_MS) {
      console.log(`[InventoryStatusSync] Throttling sync for ${sku}`);
      return;
    }
    this.syncThrottle.set(sku, Date.now());

    try {
      // Find the inventory item by SKU
      const inventoryRef = collection(db, 'inventoryDatabase');
      const q = query(inventoryRef, where('sku', '==', sku));
      const snapshot = await getDocs(q);
      
      if (snapshot.empty) {
        console.log(`[InventoryStatusSync] No inventory item found for SKU: ${sku}`);
        return;
      }

      const itemDoc = snapshot.docs[0];
      const item = { id: itemDoc.id, ...itemDoc.data() } as InventoryDatabaseItem;
      
      // Check if workflow status actually changed
      if (item.workflowStatus === workflowStatus) {
        console.log(`[InventoryStatusSync] Status unchanged for ${sku}: ${workflowStatus}`);
        return;
      }

      // Define workflow status priority (higher number = more advanced in workflow)
      const statusPriority: Record<string, number> = {
        'photo_queue': 1,
        'photod': 2,
        'edited': 3,
        'draft': 4,
        'sent_for_review': 5,
        'waiting_corrections': 6,
        'final_review': 7,
        'partially_approved': 8,
        'approved': 9,
        'listed': 10,
        'sold': 11
      };

      const currentPriority = statusPriority[item.workflowStatus || 'photo_queue'] || 0;
      const newPriority = statusPriority[workflowStatus] || 0;

      // Photo log updates should NOT override product entry statuses
      // Photo log statuses: photo_queue, photod, edited (priority 1-3)
      // Product entry statuses: draft, sent_for_review, waiting_corrections, final_review, approved (priority 4-8)
      if (source === 'photo_log' && currentPriority >= 4) {
        console.log(`[InventoryStatusSync] Skipping photo_log sync for ${sku}: current status (${item.workflowStatus}, priority ${currentPriority}) is from product entry, not overriding with ${workflowStatus}`);
        return;
      }

      // Don't downgrade status (except for product_entry or shopify sources which are authoritative)
      if (source === 'photo_log' && newPriority < currentPriority) {
        console.log(`[InventoryStatusSync] Skipping downgrade for ${sku}: ${item.workflowStatus} → ${workflowStatus} from ${source}`);
        return;
      }

      // Determine tab status based on workflow
      let newTabStatus: InventoryStatus = item.status;
      if (workflowStatus === 'sold') {
        newTabStatus = 'sold';
      } else if (workflowStatus === 'listed') {
        const isWatch = item.type?.toUpperCase() === 'WAT' || item.title?.toLowerCase().includes('watch');
        newTabStatus = isWatch ? 'watch' : 'jewelry';
      } else if (['photo_queue', 'photod', 'edited', 'draft', 'sent_for_review', 'waiting_corrections', 'final_review', 'partially_approved', 'approved'].includes(workflowStatus)) {
        // Pre-listed items stay in Drafts
        newTabStatus = 'draft';
      }

      // Build workflow history entry
      const historyEntry = {
        status: workflowStatus,
        at: new Date().toISOString(),
        source,
      };
      const existingHistory: Array<{ status: string; at: string; source: string }> =
        (item as any).workflowHistory || [];

      // Update the item
      await updateInventoryDatabaseItem(item.id, {
        workflowStatus,
        status: newTabStatus,
        workflowHistory: [...existingHistory, historyEntry],
      } as any);

      console.log(`[InventoryStatusSync] ✅ Synced ${sku}: ${workflowStatus} (${source})`);
    } catch (error) {
      console.error(`[InventoryStatusSync] Error syncing ${sku}:`, error);
    }
  }

  /**
   * Determine the most accurate workflow status from all sources
   */
  private async determineWorkflowStatus(item: InventoryDatabaseItem): Promise<WorkflowStatus> {
    const sku = item.sku;
    
    try {
      // Priority order: Shopify > Product Entry > Photo Log
      
      // 1. Check Shopify (highest priority)
      const shopifyStatus = await this.checkShopifyStatus(sku);
      if (shopifyStatus) {
        return shopifyStatus;
      }

      // 2. Check Product Entry
      try {
        const jewelryQuery = query(
          collection(db, 'jewelry_products'),
          where('sku', '==', sku)
        );
        const jewelrySnap = await getDocs(jewelryQuery);
        
        if (!jewelrySnap.empty) {
          const productEntry = jewelrySnap.docs[0].data();
          const entryStatus = productEntry.status;
          const shopifyPushStatus = productEntry.shopifyPushStatus;
          const shopifyProductId = productEntry.shopifyProductId;
          
          // Check if item is on Shopify (has product ID or successful push) - this should be 'listed'
          if (shopifyProductId || shopifyPushStatus === 'success' || shopifyPushStatus === 'partial_success' || shopifyPushStatus === 'completed') {
            return 'listed';
          }
          
          if (entryStatus === 'approved' || entryStatus === 'ready_to_list') return 'approved';
          if (entryStatus === 'partially_approved') return 'partially_approved';
          if (entryStatus === 'final_review') return 'final_review';
          if (entryStatus === 'needs_revision' || entryStatus === 'revision_requested' || entryStatus === 'corrections_needed') return 'waiting_corrections';
          if (entryStatus === 'pending' || entryStatus === 'pending_review' || entryStatus === 'in_review') return 'sent_for_review';
          if (entryStatus === 'draft') return 'draft';
        }
        
        // Also check jewelryProductEntries collection
        const entriesQuery = query(
          collection(db, 'jewelryProductEntries'),
          where('sku', '==', sku)
        );
        const entriesSnap = await getDocs(entriesQuery);
        
        if (!entriesSnap.empty) {
          const productEntry = entriesSnap.docs[0].data();
          const entryStatus = productEntry.status;
          const shopifyPushStatus = productEntry.shopifyPushStatus;
          const shopifyProductId = productEntry.shopifyProductId;
          
          // Check if item is on Shopify (has product ID or successful push) - this should be 'listed'
          if (shopifyProductId || shopifyPushStatus === 'success' || shopifyPushStatus === 'partial_success' || shopifyPushStatus === 'completed') {
            return 'listed';
          }
          
          if (entryStatus === 'approved' || entryStatus === 'ready_to_list') return 'approved';
          if (entryStatus === 'partially_approved') return 'partially_approved';
          if (entryStatus === 'final_review') return 'final_review';
          if (entryStatus === 'needs_revision' || entryStatus === 'revision_requested' || entryStatus === 'corrections_needed') return 'waiting_corrections';
          if (entryStatus === 'pending' || entryStatus === 'pending_review' || entryStatus === 'in_review') return 'sent_for_review';
          if (entryStatus === 'draft') return 'draft';
        }
      } catch (e) {
        console.log('[InventoryStatusSync] Product entry check skipped:', e);
      }

      // 3. Check Photo Log
      try {
        const now = new Date();
        const months = [
          `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`,
          `${now.getFullYear()}-${String(now.getMonth()).padStart(2, '0') || '12'}`
        ];
        
        for (const month of months) {
          const items = await photoLogService.getItemsByMonth(month);
          const photoItem = items.find(p => p.sku === sku);
          if (photoItem) {
            if (photoItem.status === 'edited') return 'edited';
            if (photoItem.status === 'photod') return 'photod';
            if (photoItem.status === 'queue') return 'photo_queue';
          }
        }
      } catch (e) {
        console.log('[InventoryStatusSync] Photo log check skipped:', e);
      }

      // Default to current status
      return item.workflowStatus || 'photo_queue';
    } catch (error) {
      console.error('[InventoryStatusSync] Error determining workflow status:', error);
      return item.workflowStatus || 'photo_queue';
    }
  }

  /**
   * Sync all inventory items (bulk sync)
   */
  async syncAll(showToasts: boolean = true): Promise<{ synced: number; errors: number }> {
    console.log('[InventoryStatusSync] Starting bulk sync...');
    
    if (showToasts) {
      toast.loading('Syncing all items...', { id: 'bulk-sync' });
    }

    let synced = 0;
    let errors = 0;

    try {
      const items = await getAllInventory();
      console.log(`[InventoryStatusSync] Syncing ${items.length} items...`);

      // Process items in batches to avoid overwhelming Firestore
      const BATCH_SIZE = 10;
      for (let i = 0; i < items.length; i += BATCH_SIZE) {
        const batch = items.slice(i, i + BATCH_SIZE);
        
        await Promise.allSettled(
          batch.map(async (item) => {
            try {
              const newWorkflowStatus = await this.determineWorkflowStatus(item);
              
              if (newWorkflowStatus !== item.workflowStatus) {
                // Determine tab status
                let newTabStatus: InventoryStatus = item.status;
                if (newWorkflowStatus === 'sold') {
                  newTabStatus = 'sold';
                } else if (newWorkflowStatus === 'listed') {
                  const isWatch = item.type?.toUpperCase() === 'WAT' || item.title?.toLowerCase().includes('watch');
                  newTabStatus = isWatch ? 'watch' : 'jewelry';
                } else {
                  newTabStatus = 'draft';
                }
                
                await updateInventoryDatabaseItem(item.id, {
                  workflowStatus: newWorkflowStatus,
                  status: newTabStatus
                });

                // LFAC: Log status change to sku_history (non-blocking)
                import('./inventoryEventService').then(({ inventoryEventService }) => {
                  inventoryEventService.logStatusChange({
                    sku: item.sku,
                    barcode: item.barcode,
                    inventoryItemId: item.id,
                    previousStatus: item.workflowStatus || item.status,
                    newStatus: newWorkflowStatus,
                    statusField: 'workflowStatus',
                    userId: 'system',
                    userName: 'Inventory Sync',
                    source: 'inventory_sync',
                  });
                }).catch(() => {});
                
                synced++;
                console.log(`[InventoryStatusSync] Synced ${item.sku}: ${item.workflowStatus} → ${newWorkflowStatus}`);
              }
            } catch (error) {
              errors++;
              console.error(`[InventoryStatusSync] Error syncing ${item.sku}:`, error);
            }
          })
        );
      }

      if (showToasts) {
        if (synced > 0) {
          toast.success(`Synced ${synced} item${synced !== 1 ? 's' : ''}`, { id: 'bulk-sync' });
        } else {
          toast.success('All items are up to date', { id: 'bulk-sync' });
        }
        
        if (errors > 0) {
          toast.error(`${errors} error${errors !== 1 ? 's' : ''} occurred`, { duration: 5000 });
        }
      }

      console.log(`[InventoryStatusSync] Bulk sync complete: ${synced} synced, ${errors} errors`);
    } catch (error) {
      console.error('[InventoryStatusSync] Bulk sync error:', error);
      if (showToasts) {
        toast.error('Bulk sync failed', { id: 'bulk-sync' });
      }
      errors++;
    }

    return { synced, errors };
  }

  /**
   * Start periodic background sync
   */
  private startPeriodicSync(intervalMs: number): void {
    if (this.periodicSyncInterval) {
      console.log('[InventoryStatusSync] Periodic sync already running');
      return;
    }

    console.log(`[InventoryStatusSync] Starting periodic sync (every ${intervalMs / 1000}s)`);
    
    this.periodicSyncInterval = setInterval(async () => {
      console.log('[InventoryStatusSync] Running periodic sync...');
      await this.syncAll(false); // Don't show toasts for periodic sync
    }, intervalMs);
  }

  /**
   * Stop periodic background sync
   */
  stopPeriodicSync(): void {
    if (this.periodicSyncInterval) {
      clearInterval(this.periodicSyncInterval);
      this.periodicSyncInterval = null;
      console.log('[InventoryStatusSync] Periodic sync stopped');
    }
  }

  /**
   * Manually sync a single item (used by the UI)
   */
  async syncSingleItem(item: InventoryDatabaseItem): Promise<void> {
    const newWorkflowStatus = await this.determineWorkflowStatus(item);
    
    if (newWorkflowStatus !== item.workflowStatus) {
      let newTabStatus: InventoryStatus = item.status;
      if (newWorkflowStatus === 'sold') {
        newTabStatus = 'sold';
      } else if (newWorkflowStatus === 'listed') {
        const isWatch = item.type?.toUpperCase() === 'WAT' || item.title?.toLowerCase().includes('watch');
        newTabStatus = isWatch ? 'watch' : 'jewelry';
      } else {
        newTabStatus = 'draft';
      }
      
      await updateInventoryDatabaseItem(item.id, {
        workflowStatus: newWorkflowStatus,
        status: newTabStatus
      });

      // LFAC: Log status change to sku_history (non-blocking)
      import('./inventoryEventService').then(({ inventoryEventService }) => {
        inventoryEventService.logStatusChange({
          sku: item.sku,
          barcode: item.barcode,
          inventoryItemId: item.id,
          previousStatus: item.workflowStatus || item.status,
          newStatus: newWorkflowStatus,
          statusField: 'workflowStatus',
          userId: 'system',
          userName: 'Manual Sync',
          source: 'inventory_sync',
        });
      }).catch(() => {});
      
      toast.success(`Status updated to ${newWorkflowStatus}`);
    } else {
      toast.success('Status is up to date');
    }
  }
}

// Export singleton instance
export const inventoryStatusSyncService = InventoryStatusSyncService.getInstance();
